export const RATE_PERIODS = ['second', 'minute', 'hour', 'day'] as const;
export type RatePeriod = typeof RATE_PERIODS[number];

export function parseCustomRate(value: string): { count: number; period: RatePeriod } | null {
  const match = /^(\d+)\/(second|minute|hour|day)$/.exec(value.trim().toLowerCase());
  if (!match) return null;
  const count = Number(match[1]);
  return Number.isInteger(count) && count > 0 && count <= 2147483647
    ? { count, period: match[2] as RatePeriod } : null;
}

export function retryAfterMs(value: string | null, now = Date.now()): number | null {
  if (!value?.trim()) return null;
  const seconds = Number(value);
  if (Number.isFinite(seconds)) return seconds >= 0 ? seconds * 1000 : null;
  const date = Date.parse(value);
  return Number.isFinite(date) ? Math.max(0, date - now) : null;
}

interface BackoffState { count: number; lastAttempt: number; backoffUntil: number }
export class BackoffManager {
  private backoffStates = new Map<string, BackoffState>();
  shouldWait(key: string): number {
    return Math.max(0, (this.backoffStates.get(key)?.backoffUntil ?? 0) - Date.now());
  }
  addFailure(key: string, isRateLimit = false, retryMs: number | null = null): number {
    const now = Date.now();
    const state = this.backoffStates.get(key) ?? { count: 0, lastAttempt: 0, backoffUntil: 0 };
    if (now - state.lastAttempt > 60000) state.count = 0;
    state.count++;
    state.lastAttempt = now;
    const fallback = Math.min((isRateLimit ? 5000 : 1000) * Math.pow(2, state.count), 60000);
    const delay = Math.min(retryMs ?? fallback, 2147483647);
    state.backoffUntil = now + delay;
    this.backoffStates.set(key, state);
    return delay;
  }
  clearFailures(key: string) { this.backoffStates.delete(key); }
}
